<template>
    <div class="kpi-card" :class="[type, { active, disabled }]" @click="handleClick">
        <div class="kpi-icon">
            <i :class="[iconClassName, iconSizeClass]"></i>
        </div>
        <div class="kpi-content">
            <div class="kpi-label">{{ label }}</div>
            <div class="kpi-value">
                <template v-if="hasSlash">
                    <span class="main-value">{{ formatDisplayNumber(firstPart) }}</span>
                    <span class="secondary-value">{{ secondPart }}</span>
                </template>
                <template v-else>
                    {{ formatDisplayNumber(value) }}
                </template>
            </div>
        </div>
        <div class="kpi-link">
            <i v-if="linkIconClass" class="kpi-link-icon" :class="linkIconClassName"></i>
            <template v-else>{{ linkText }}</template>
        </div>
    </div>
</template>

<script>
import { formatNumberLabel } from '@/utils/numberFormat'
export default {
    name: 'KPICard',
    props: {
        type: {
            type: String,
            required: true
        },
        iconClass: {
            type: String,
            required: true
        },
        label: {
            type: String,
            required: true
        },
        value: {
            type: [String, Number],
            required: true
        },
        total: {
            type: [String, Number],
            default: null
        },
        linkText: {
            type: String,
            required: true
        },
        linkIconClass: {
            type: String,
            default: ''
        },
        iconSize: {
            type: String,
            default: 'l'
        },
        active: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        iconClassName() {
            return this.iconClass.indexOf('i-andy-') === 0 ? this.iconClass : 'i-andy-' + this.iconClass
        },
        iconSizeClass() {
            return this.iconSize.indexOf('i-andy-') === 0 ? this.iconSize : 'i-andy-' + this.iconSize
        },
        linkIconClassName() {
            return this.linkIconClass.indexOf('i-andy-') === 0 ? this.linkIconClass : 'i-andy-' + this.linkIconClass
        },
        hasTotal() {
            return this.total !== null && this.total !== undefined && this.total !== ''
        },
        hasSlash() {
            return this.hasTotal || String(this.value).includes('/')
        },
        firstPart() {
            if (this.hasTotal) return this.value
            if (!this.hasSlash) return this.value
            return String(this.value).split('/')[0]
        },
        secondPart() {
            if (this.hasTotal) return '/' + this.formatDisplayNumber(this.total)
            if (!this.hasSlash) return ''
            const parts = String(this.value).split('/')
            return '/' + parts.slice(1).map(part => this.formatDisplayNumber(part.trim())).join('/')
        }
    },
    methods: {
        formatDisplayNumber(value, fractionDigits) {
            return formatNumberLabel(value, this.$i18n.locale, fractionDigits)
        },
        handleClick() {
            if (this.disabled) return
            this.$emit('click', this.type)
        }
    }
}
</script>

<style lang="scss" scoped>
.kpi-card {
    display: flex;
    flex: 1 0 0;
    min-width: 200px;
    border-radius: 4px;
    border: 1px solid var(--neutro-dark-app, #c4ccd9);
    background: var(--neutro-light-app, #fff);
    padding: 16px;
    @include display-flex();
    align-items: center;
    gap: 16px;
    position: relative;
    cursor: pointer;

    .kpi-icon {
        width: 40px;
        height: 40px;
        @include border-radius(4px);
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
        flex-shrink: 0;

        i {
            font-size: 24px;
        }
    }

    &.active {
        background: #eee;
    }

    &.disabled {
        pointer-events: none;
        cursor: default;
    }

    @media (hover: hover) {
        &:not(.disabled):hover {
            border-color: $main;
        }
    }

    .kpi-content {
        flex: 1;
        gap: 8px;
        @include display-flex();
        @include flex-direction(column);
        @include align-items(start);

        .kpi-label {
            @include font-size(sm);
            color: #222;
            font-family: Avenir-Regular;
        }

        .kpi-value {
            font-size: 24px;
            font-family: Avenir-Bold;
            color: #333;

            .main-value {
                font-size: 24px;
                font-family: Avenir-Bold;
            }

            .secondary-value {
                font-size: 16px;
                font-family: Avenir-Regular;
                font-weight: 400;
            }
        }
    }

    .kpi-link {
        position: absolute;
        bottom: 16px;
        right: 16px;
        color: $main-text;
        font-size: 12px;
        font-weight: 700;
        font-family: Avenir-Bold;

        .kpi-link-icon {
            @include display-flex();
            @include align-items(center);
            @include justify-content(center);
            width: 16px;
            height: 16px;
            color: inherit;
            font-size: 16px;
            line-height: 16px;
        }
    }

    &.issues {
        .kpi-icon {
            background-color: $error-t90;

            i {
                color: $error-dark;
            }
        }
    }

    &.alerts {
        .kpi-icon {
            background-color: $accent-t90;

            i {
                color: #cc9f00;
            }
        }
    }

    &.connected {
        .kpi-icon {
            background-color: $done-t90;

            i {
                color: $done;
            }
        }
    }

    &.disconnected {
        .kpi-icon {
            background-color: $error-t90;

            i {
                color: $error-dark;
            }
        }
    }

    &.critical {
        .kpi-icon {
            width: 44px;
            height: 44px;
            padding: 4px;
            box-sizing: border-box;
            gap: 8px;
            background-color: #bd1f1e;

            i {
                @include display-flex();
                @include align-items(center);
                @include justify-content(center);
                width: 36px;
                height: 36px;
                color: #fcefef;
                font-size: 36px;
                line-height: 36px;
            }
        }
    }

    &.offline {
        .kpi-icon {
            background-color: $neutro-t80;

            i {
                color: $neutro-s30;
            }
        }
    }

    // Active state - when KPI is selected as filter
    &.active {
        border: 1px solid $main;
        background: $main-t90;

        .kpi-link {
            color: $main;
            font-weight: 700;
        }
    }
}

// Mobile/Portrait responsive styles for horizontal scroll
// Apply for tablets and medium screens (up to 1100px)
@include media('<=1100px') {
    .kpi-card:not(.critical) {
        flex-wrap: wrap;
        min-width: 220px !important;
        width: 220px !important;

        .kpi-icon {
            order: 1;
            flex: 0 0 auto;
        }
        .kpi-content {
            order: 2;
            flex: 1 1 auto;
            min-width: 0;
        }
        .kpi-link {
            position: static;
            order: 3;
            flex: 0 0 100%;
            width: 100%;
            margin-top: 8px;
            text-align: left;
        }
    }
}

@media (max-width: 679px) {
    .kpi-card.critical {
        flex-wrap: nowrap;
        align-items: flex-start;
        min-width: 0 !important;
        width: auto !important;
        min-height: 128px;
        padding: 12px;
        gap: 0;

        .kpi-icon {
            display: none;
        }

        .kpi-content {
            order: 1;
            width: 100%;
            height: 100%;
            gap: 12px;
            @include justify-content(space-between);

            .kpi-label {
                width: 100%;
                font-size: 20px;
                line-height: 24px;
            }

            .kpi-value {
                padding-right: 28px;
                color: #bd1f1e;
                font-size: 36px;
                line-height: 40px;

                .main-value {
                    color: #bd1f1e;
                    font-size: 36px;
                    line-height: 40px;
                }

                .secondary-value {
                    color: #222;
                    font-size: 24px;
                    line-height: 32px;
                }
            }
        }

        .kpi-link {
            position: absolute;
            right: 12px;
            bottom: 12px;
            order: initial;
            flex: none;
            width: 16px;
            margin-top: 0;
            text-align: initial;
        }
    }
}
</style>
